import clsx from "clsx";
import { FlexAligner } from "../FlexAligner";
import { type SizeToken } from "src/models/types/common.types";
import styling from "./Avatar.module.scss";
import { TextDynamic } from "@scaleaq/sui-react";

export type AvatarProps = {
	className?: string;
	label: string;
	icon?: React.ReactNode | string;
	imageSrc?: string;
	imageAlt?: string;
	size: Extract<SizeToken, "small" | "medium" | "large">;
	color?: "info" | "teal" | "lime" | "magenta" | "orange" | "purple" | "neutral";
	disabled?: boolean;
	active?: boolean;
	interactive?: boolean;
};

export const Avatar = (props: AvatarProps) => {
	const { className, label, size, icon, imageAlt, imageSrc, color = "info", disabled, active, interactive } = props;

	const _getTypographyType = () => {
		switch (size) {
			case "small":
				return "body-small";
			case "medium":
				return "body-medium";
			case "large":
				return "body-large";
		}
	};

	const _getInitials = (text: string) => {
		if (text.length > 3) {
			return text
				.split(" ")
				.map((word) => word[0])
				.join("");
		} else {
			return text;
		}
	};

	const _renderText = () => {
		if (label && !imageSrc) {
			return (
				<TextDynamic type={_getTypographyType()} className={styling["text"]} weight="semi-bold" color={"inherit"} align="center" uppercase>
					{_getInitials(label)}
				</TextDynamic>
			);
		}
	};

	const _renderImage = () => {
		if (imageSrc) {
			if (!imageAlt) {
				console.warn("Avatar component: 'imageAlt' prop is required when 'imageSrc' is provided for accessibility reasons.");
			}

			return <img src={imageSrc} alt={imageAlt || ""} className={styling["image"]} />;
		}
	};

	const _renderIcon = () => {
		if (icon) {
			return <span className={styling["icon"]}>{icon}</span>;
		}
	};

	return (
		<FlexAligner
			direction="row"
			alignment="center"
			className={clsx(styling["root"], {
				[`${styling[`root-${color}`]}`]: color,
				[`${styling[`root-${color}--disabled`]}`]: color && disabled,
				[`${styling[`root-${color}--active`]}`]: color && active,
				[`${styling[`root-${color}--interactive`]}`]: interactive,
				[`${styling[`size`]}`]: size,
				[`${styling[`size--${size}`]}`]: size,
				[`${className}`]: className,
			})}
		>
			{_renderText()}
			{_renderImage()}
			{_renderIcon()}
		</FlexAligner>
	);
};
